import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";

export default function OurApproach() {
  return (
    <div data-testid="page-our-approach">
      <Seo
        title="Our Approach | SymbiCognitive"
        description="Roots before fruits — how durable organizational improvements develop, and the principles behind practical, trustworthy AI adoption."
        path="/our-approach"
      />
      <section className="bg-midnight pt-20" data-testid="approach-hero">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-16">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-teal">Our Approach</span>
          <h1 className="font-display font-extrabold text-white text-4xl sm:text-5xl tracking-tight mt-4 leading-[1.05]">Roots before fruits.</h1>
          <p className="mt-6 text-lg text-amber font-display font-semibold">When it matters to you, you build it to last.</p>
        </div>
        <div className="gradient-divider h-1.5 w-full" />
      </section>

      <section className="bg-white py-24 sm:py-28" data-testid="approach-body">
        <div className="max-w-3xl mx-auto px-6 sm:px-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="space-y-6 text-[var(--slate-soft)] text-lg leading-relaxed"
          >
            <p>My grandfather planted the windbreak around his Wisconsin farm before he built the house. He understood the value of preparing, tending, and caring for things that needed to endure.</p>
            <p>That lesson has followed me throughout my career. Organizations thrive when their people understand what matters, have the tools to do good work, and take pride in improving the place they share.</p>
            <p>I believe the same principles belong in the conversation about artificial intelligence. A useful AI tool needs dependable information, thoughtful oversight, capable people, and a process that makes sense. Each improvement gives us an opportunity to learn, strengthen the work, and build on what succeeds.</p>
            <p className="text-slateink font-medium">That's the thinking behind SymbiCognitive.</p>
          </motion.div>

          <div className="mt-16 border-t border-slate-200 pt-12">
            <h2 className="font-display font-bold text-slateink text-2xl sm:text-3xl tracking-tight">How improvements take root</h2>
            <ol className="mt-8 space-y-8">
              {[
                ["Find the friction", "Understand how the work happens today — where tasks stall, what gets entered twice, and which handoffs cause confusion. Understanding the work is the starting point for improving it."],
                ["Test practical improvements", "Begin with one clearly defined problem. Explore how AI assists with gathering information, drafting documents, or organizing work. Start small. Test carefully. Measure what changes."],
                ["Help the improvement take root", "A promising tool needs clear responsibilities, trustworthy information, and people who know how to use it. Training, communication, and accountability turn a tool into everyday practice."],
              ].map(([t, d], i) => (
                <li key={i} className="flex gap-5" data-testid={`approach-step-${i}`}>
                  <span className="shrink-0 h-10 w-10 rounded-full bg-midnight text-amber font-display font-bold grid place-items-center">{i + 1}</span>
                  <div>
                    <h3 className="font-display font-semibold text-slateink text-lg">{t}</h3>
                    <p className="mt-1 text-[var(--slate-soft)] leading-relaxed">{d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-14">
            <Link to="/contact" data-testid="approach-cta" className="group inline-flex items-center gap-2 bg-midnight text-white font-semibold px-6 py-3.5 rounded-xl hover:bg-slate-800 transition-colors">
              Start a conversation
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
